import { Loader2, MessageSquare } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { cn } from "@/lib/utils";

export interface ChatMessage {
  id: string;
  authorId: string;
  authorName: string;
  authorRole?: string;
  text: string;
  createdAt: Date;
}

export interface ChatDiscussionProps {
  messages: ChatMessage[];
  currentUserId: string;
  currentUserName?: string;
  onSend: (text: string) => void | Promise<void>;
  isSending?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
  readOnlyMessage?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  placeholder?: string;
  height?: string;
  size?: "sm" | "md";
}

const formatTimestamp = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = diff / (1000 * 60 * 60);
  if (hours < 24)
    return `Today ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  if (hours < 48)
    return `Yesterday ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const ChatDiscussion: React.FC<ChatDiscussionProps> = ({
  messages,
  currentUserId,
  currentUserName,
  onSend,
  isSending = false,
  isLoading = false,
  readOnly = false,
  readOnlyMessage = "You have view-only access to this discussion.",
  emptyStateTitle = "No messages yet",
  emptyStateDescription = "Start the discussion!",
  placeholder = "Type a message\u2026 (Enter to send)",
  height = "h-80",
  size = "md",
}) => {
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { getAvatarUrlByName } = useUserPreferences();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!newMessage.trim() || isSending) return;
    onSend(newMessage.trim());
    setNewMessage("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isSmall = size === "sm";
  const avatarSize = isSmall ? "xs" : "sm";
  const bubbleText = isSmall ? "text-xs" : "text-sm";
  const metaText = isSmall ? "text-[10px]" : "text-xs";

  return (
    <div
      className={cn(
        "flex flex-col bg-muted/30 rounded-lg border overflow-hidden",
        height,
      )}
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 mb-2 animate-spin opacity-50" />
            <p className="text-sm">Loading messages\u2026</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm font-medium">{emptyStateTitle}</p>
            <p className="text-xs">{emptyStateDescription}</p>
          </div>
        ) : (
          <>
            {messages.map((message) => {
              const isMe = message.authorId === currentUserId;
              const displayName =
                isMe && currentUserName
                  ? `You (${currentUserName})`
                  : isMe
                    ? `You (${message.authorName})`
                    : message.authorName;

              return (
                <div
                  key={message.id}
                  className={cn(
                    "flex gap-2",
                    isMe ? "flex-row-reverse" : "flex-row",
                  )}
                >
                  <UserAvatar
                    name={message.authorName}
                    seed={message.authorName}
                    avatarUrl={getAvatarUrlByName(message.authorName)}
                    size={avatarSize}
                  />
                  <div
                    className={cn(
                      "flex flex-col max-w-[75%]",
                      isMe ? "items-end" : "items-start",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center gap-2 mb-1 text-muted-foreground",
                        metaText,
                        isMe && "flex-row-reverse",
                      )}
                    >
                      <span className="font-medium">{displayName}</span>
                      <span className="text-muted-foreground/60">
                        {formatTimestamp(message.createdAt)}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "rounded-2xl px-3 py-2",
                        bubbleText,
                        isMe
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-background border rounded-tl-sm",
                      )}
                    >
                      <p className="whitespace-pre-wrap break-words">
                        {message.text}
                      </p>
                    </div>
                    {message.authorRole && (
                      <p
                        className={cn(
                          "text-muted-foreground/60 mt-0.5",
                          metaText,
                        )}
                      >
                        {message.authorRole}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {readOnly ? (
        <div className="border-t bg-muted/30 p-3 text-center">
          <p className="text-sm text-muted-foreground">{readOnlyMessage}</p>
        </div>
      ) : (
        <div className="border-t bg-background p-3 flex gap-2">
          <Textarea
            placeholder={placeholder}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            className="resize-none flex-1 min-h-0"
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!newMessage.trim() || isSending}
            className="self-end"
          >
            {isSending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              "Send"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export { ChatDiscussion };
